import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { DataTable, ExportButtons, FIN_COLS, Q, kfmt } from "./shared";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

export const VendorsTab = ({ params }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    setRows(null);
    api.get("/analytics/vendors", { params: { date_from: params.date_from, date_to: params.date_to } })
      .then((r) => { if (live) setRows(r.data.rows); }).catch((e) => live && toast.error(formatApiError(e.response?.data?.detail)));
    return () => { live = false; };
  }, [params]);
  if (!rows) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const cols = [{ key: "name", label: "Vendedor" }, { key: "docs", label: "Ventas", num: true }, ...FIN_COLS,
    { key: "quotes", label: "Cotiz.", num: true }, { key: "won", label: "Ganadas", num: true }, { key: "conversion", label: "Conversión", pct: true }];
  return (
    <div className="space-y-3" data-testid="analytics-vendors">
      <div className="flex justify-end"><ExportButtons tab="vendors" params={params} /></div>
      <Card className="rounded-sm border shadow-none p-4"><ResponsiveContainer width="100%" height={240}>
        <BarChart data={rows.filter((r) => r.venta > 0)}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={42} tickFormatter={kfmt} />
          <Tooltip formatter={(v) => Q(v)} /><Bar dataKey="venta" name="Venta" fill="hsl(var(--primary))" /><Bar dataKey="utilidad" name="Utilidad" fill="#16a34a" /></BarChart>
      </ResponsiveContainer></Card>
      <DataTable cols={cols} rows={rows} testid="vendors-table" />
    </div>
  );
};

const Row = ({ l }) => {
  const strong = ["utilidad_bruta", "utilidad_neta", "venta_total"].includes(l.key);
  return (
    <tr className={`border-t border-border ${strong ? "bg-muted/40 font-bold" : ""}`} data-testid={`income-line-${l.key}`}>
      <td className="px-4 py-2.5">{l.label}</td>
      <td className={`px-4 py-2.5 text-right font-mono ${l.key === "utilidad_neta" ? (l.amount >= 0 ? "text-green-700" : "text-destructive") : ""}`}>{Q(l.amount)}</td>
      <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground">{l.pct_sales}%</td>
      <td className="px-4 py-2.5 text-right font-mono text-xs">{Q(l.prev)}</td>
      <td className={`px-4 py-2.5 text-right font-mono text-xs ${l.diff >= 0 ? "text-green-700" : "text-destructive"}`}>{l.diff_pct == null ? "—" : `${l.diff_pct > 0 ? "+" : ""}${l.diff_pct}%`}</td>
    </tr>
  );
};

const Breakdown = ({ title, items }) => (
  <Card className="rounded-sm border shadow-none p-4">
    <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{title}</h4>
    {items.length === 0 ? <p className="text-sm text-muted-foreground">Sin registros.</p> : items.map((x) => (
      <div key={x.concept} className="flex justify-between text-sm py-1 border-b border-border last:border-0"><span>{x.concept}</span><span className="font-mono">{Q(x.amount)}</span></div>))}
  </Card>
);

export const IncomeTab = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    setD(null);
    api.get("/analytics/income-statement", { params: { month } }).then((r) => { if (live) setD(r.data); }).catch((e) => live && toast.error(formatApiError(e.response?.data?.detail)));
    return () => { live = false; };
  }, [month]);
  return (
    <div className="space-y-4" data-testid="analytics-income">
      <div className="flex flex-wrap items-end gap-2 justify-between">
        <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Mes
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="rounded-sm h-9 w-48 mt-1" data-testid="income-month" /></label>
        <ExportButtons tab="income" params={{ month }} />
      </div>
      {!d ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (<>
        <Card className="rounded-sm border shadow-none overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]" data-testid="income-table">
            <thead className="bg-muted text-[11px] uppercase tracking-widest text-muted-foreground"><tr>
              <th className="px-4 py-2.5 text-left">Concepto</th><th className="px-4 py-2.5 text-right">{d.label}</th><th className="px-4 py-2.5 text-right">% ventas</th>
              <th className="px-4 py-2.5 text-right">{d.prev_label}</th><th className="px-4 py-2.5 text-right">Variación</th></tr></thead>
            <tbody>{d.lines.map((l) => <Row key={l.key} l={l} />)}</tbody>
          </table>
        </Card>
        <p className="text-xs text-muted-foreground">Margen bruto {d.margen_bruto}% · Margen neto {d.margen_neto}% · Punto de equilibrio {Q(d.punto_equilibrio)} · Compras de repuestos del mes {Q(d.compras_repuestos)} (informativo). Incluye cifras manuales históricas si existen.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <Breakdown title="Gastos fijos" items={d.gastos_fijos_det} />
          <Breakdown title="Gastos variables" items={d.gastos_variables_det} />
        </div>
      </>)}
    </div>
  );
};
